import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: 'Helvetica',
    backgroundColor: '#fff'
  },
  topSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20
  },
  invoiceBox: {
    backgroundColor: '#0E835A',
    padding: 12,
    borderRadius: 3
  },
  invoiceText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
    letterSpacing: 3
  },
  dateNumberSection: {
    textAlign: 'right'
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 5
  },
  label: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#000',
    marginRight: 5
  },
  value: {
    fontSize: 9,
    color: '#000'
  },
  logoOrgSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 20,
    gap: 15
  },
  logo: {
    width: 60,
    height: 60,
    objectFit: 'contain'
  },
  orgTextBox: {
    textAlign: 'right',
    maxWidth: 220,
    wordBreak: 'break-word',
    flexWrap: 'wrap'
  },
  orgName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0E835A',
    marginBottom: 2
  },
  orgSubtitle: {
    fontSize: 8,
    color: '#666'
  },
  recipientSection: {
    marginBottom: 20,
    lineHeight: 1.6
  },
  recipientRow: {
    flexDirection: 'row',
    marginBottom: 4
  },
  recipientLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#000',
    width: 70
  },
  recipientValue: {
    fontSize: 9,
    color: '#000',
    flex: 1,
    wordBreak: 'break-word',
    maxWidth: '100%',
    flexWrap: 'wrap'
  },
  table: {
    marginBottom: 10
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderWidth: 1,
    borderColor: '#000',
    padding: 6
  },
  thText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#000',
    textTransform: 'uppercase'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: '#000',
    minHeight: 25,
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 6
  },
  tdText: {
    fontSize: 9,
    color: '#000',
    wordBreak: 'break-word',
    maxWidth: '100%',
    flexWrap: 'wrap'
  },
  totalRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#000',
    minHeight: 30,
    alignItems: 'center'
  },
  totalLabelCell: {
    width: '80%',
    paddingHorizontal: 6,
    borderRightWidth: 1,
    borderRightColor: '#000'
  },
  totalAmountCell: {
    width: '20%',
    paddingHorizontal: 6,
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  totalAmountText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000'
  },
  amountInWords: {
    fontSize: 8,
    color: '#000',
    fontStyle: 'italic'
  },
  descriptionBox: {
    marginTop: 15,
    marginBottom: 20,
    paddingHorizontal: 2,
    maxWidth: '100%',
    minHeight: 40
  },
  descText: {
    fontSize: 9,
    color: '#000',
    lineHeight: 1.5,
    textAlign: 'justify',
    wordBreak: 'break-word',
    maxWidth: '100%',
    flexWrap: 'wrap'
  },
  sincerelyText: {
    fontSize: 8,
    color: '#000',
    marginTop: 8,
    textAlign: 'right'
  },
  signatureSection: {
    marginTop: 30,
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 30
  },
  signatureBox: {
    flex: 1,
    alignItems: 'center'
  },
  signatureLabel: {
    fontSize: 8,
    color: '#666',
    marginBottom: 6,
    textTransform: 'uppercase'
  },
  signatureValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000',
    textAlign: 'center',
    wordBreak: 'break-word',
    maxWidth: '100%',
    flexWrap: 'wrap'
  }
});

const KurbanReportPDF = ({ data, logos }) => {
  // Sayıyı kelimeye çevirme (basitleştirilmiş)
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero US dollars';
    const units = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
    const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const thousands = ['', 'thousand', 'million'];

    const convertHundreds = (n) => {
      if (n === 0) return '';
      if (n < 10) return units[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + units[n % 10] : '');
      return units[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' ' + convertHundreds(n % 100) : '');
    };

    let result = '';
    let groupIndex = 0;
    while (num > 0) {
      const group = num % 1000;
      if (group !== 0) {
        result = convertHundreds(group) + (thousands[groupIndex] ? ' ' + thousands[groupIndex] : '') + (result ? ' ' + result : '');
      }
      num = Math.floor(num / 1000);
      groupIndex++;
    }
    return '#' + result.trim() + ' US dollars#';
  };

  const totalInWords = data.totalIncome ? numberToWords(Math.floor(data.totalIncome)) : '#Zero US dollars#';

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* 1. Üst Kısım - INVOICE ve Tarih/Numara */}
        <View style={styles.topSection}>
          <View style={styles.invoiceBox}>
            <Text style={styles.invoiceText}>INVOICE</Text>
          </View>
          <View style={styles.dateNumberSection}>
            <View style={styles.dateRow}>
              <Text style={styles.label}>Invoice Date:</Text>
              <Text style={styles.value}>{data.invoiceDate || data.date || new Date().toLocaleDateString()}</Text>
            </View>
            <View style={styles.dateRow}>
              <Text style={styles.label}>Invoice Number:</Text>
              <Text style={styles.value}>{data.invoiceNumber || `KRB-${data.projectYear || new Date().getFullYear()}-001`}</Text>
            </View>
          </View>
        </View>

        {/* 2. Logo ve Organizasyon Bilgileri */}
        <View style={styles.logoOrgSection}>
          {logos.length > 0 && logos[0] && (
            <Image src={logos[0].data} style={styles.logo} />
          )}
          <View style={styles.orgTextBox}>
            <Text style={styles.orgName}>{data.organizationName || 'Organization Name'}</Text>
            <Text style={styles.orgSubtitle}>{data.orgSubtitle || 'Relief Foundation'}</Text>
          </View>
        </View>

        {/* 3. Alıcı Bilgileri */}
        <View style={styles.recipientSection}>
          <View style={styles.recipientRow}>
            <Text style={styles.recipientLabel}>Name:</Text>
            <Text style={styles.recipientValue}>{data.clientName || 'N/A'}</Text>
          </View>
          <View style={styles.recipientRow}>
            <Text style={styles.recipientLabel}>Tax Office:</Text>
            <Text style={styles.recipientValue}>{data.taxOffice || 'N/A'}</Text>
          </View>
          <View style={styles.recipientRow}>
            <Text style={styles.recipientLabel}>Tax Number:</Text>
            <Text style={styles.recipientValue}>{data.taxNumber || 'N/A'}</Text>
          </View>
          <View style={styles.recipientRow}>
            <Text style={styles.recipientLabel}>Address:</Text>
            <Text style={styles.recipientValue}>{data.clientAddress || 'N/A'}</Text>
          </View>
        </View>

        {/* 4. Income Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.thText, { width: '8%', textAlign: 'center' }]}>S.N</Text>
            <Text style={[styles.thText, { width: '32%' }]}>Donor Name</Text>
            <Text style={[styles.thText, { width: '20%' }]}>Kind of Animal</Text>
            <Text style={[styles.thText, { width: '12%', textAlign: 'center' }]}>Qty</Text>
            <Text style={[styles.thText, { width: '14%', textAlign: 'right' }]}>Unit Price</Text>
            <Text style={[styles.thText, { width: '14%', textAlign: 'right' }]}>Total</Text>
          </View>
          {data.donations && data.donations.length > 0 ? (
            data.donations.map((donation, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tdText, { width: '8%', textAlign: 'center' }]}>{index + 1}</Text>
                <Text style={[styles.tdText, { width: '32%' }]}>{donation.donorName}</Text>
                <Text style={[styles.tdText, { width: '20%' }]}>{donation.animalType}</Text>
                <Text style={[styles.tdText, { width: '12%', textAlign: 'center' }]}>{donation.shareCount}</Text>
                <Text style={[styles.tdText, { width: '14%', textAlign: 'right' }]}>${donation.unitPrice.toFixed(2)}</Text>
                <Text style={[styles.tdText, { width: '14%', textAlign: 'right', fontWeight: 'bold' }]}>${donation.total.toFixed(2)}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={[styles.tdText, { width: '100%', textAlign: 'center', fontStyle: 'italic', color: '#999' }]}>
                No donations added yet
              </Text>
            </View>
          )}
          <View style={styles.totalRow}>
            <View style={styles.totalLabelCell}>
              <Text style={styles.amountInWords}>{totalInWords}</Text>
              <Text style={[styles.tdText, { fontWeight: 'bold', marginTop: 4 }]}>Total Income:</Text>
            </View>
            <View style={styles.totalAmountCell}>
              <Text style={styles.totalAmountText}>${data.totalIncome?.toFixed(2) || '0.00'}</Text>
            </View>
          </View>
        </View>

        {/* 5. Expenses Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.thText, { width: '8%', textAlign: 'center' }]}>S.N</Text>
            <Text style={[styles.thText, { width: '72%' }]}>Expense Item</Text>
            <Text style={[styles.thText, { width: '20%', textAlign: 'right' }]}>Amount</Text>
          </View>
          {data.expenses && data.expenses.length > 0 ? (
            data.expenses.map((expense, index) => (
              <View key={index} style={styles.tableRow}>
                <Text style={[styles.tdText, { width: '8%', textAlign: 'center' }]}>{index + 1}</Text>
                <Text style={[styles.tdText, { width: '72%' }]}>{expense.expenseItem}</Text>
                <Text style={[styles.tdText, { width: '20%', textAlign: 'right', fontWeight: 'bold' }]}>${expense.amount.toFixed(2)}</Text>
              </View>
            ))
          ) : (
            <View style={styles.tableRow}>
              <Text style={[styles.tdText, { width: '100%', textAlign: 'center', fontStyle: 'italic', color: '#999' }]}>
                No expenses added yet
              </Text>
            </View>
          )}
          <View style={styles.totalRow}>
            <View style={styles.totalLabelCell}>
              <Text style={[styles.tdText, { fontWeight: 'bold', marginTop: 4 }]}>Total Expense:</Text>
            </View>
            <View style={styles.totalAmountCell}>
              <Text style={styles.totalAmountText}>${data.totalExpense?.toFixed(2) || '0.00'}</Text>
            </View>
          </View>
        </View>

        {/* 6. Açıklama Metni - Sadece kullanıcı metni, başlık yok */}
        {/* Açıklama Metni - Project Report tam metin */}
        <View style={styles.descriptionBox}>
          <Text style={styles.descText} wrap>
            {data.projectReport && data.projectReport.trim() !== ''
              ? data.projectReport
              : (data.notes || '')}
          </Text>
        </View>

        {/* 7. Bank Information (Optional) */}
        {data.showBankInfo && (
          <View style={{ marginBottom: 20, padding: 10, border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f8f8f8' }}>
            <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#0E835A', marginBottom: 6 }}>Bank Information</Text>
            <View style={{ flexDirection: 'row', marginBottom: 3 }}>
              <Text style={{ fontSize: 8, fontWeight: 'bold', width: 90 }}>Bank Name:</Text>
              <Text style={{ fontSize: 8 }}>{data.bankName || '-'}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 3 }}>
              <Text style={{ fontSize: 8, fontWeight: 'bold', width: 90 }}>Account Name:</Text>
              <Text style={{ fontSize: 8 }}>{data.accountName || '-'}</Text>
            </View>
            <View style={{ flexDirection: 'row', marginBottom: 3 }}>
              <Text style={{ fontSize: 8, fontWeight: 'bold', width: 90 }}>Account Number / IBAN:</Text>
              <Text style={{ fontSize: 8 }}>{data.accountNumber || '-'}</Text>
            </View>
          </View>
        )}

        {/* 8. Project Balance (Proje Bakiyesi) Özet Bloğu */}
        <View style={{ marginTop: 20, marginBottom: 20, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ fontSize: 10, color: '#333' }}>Total Income</Text>
            <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#0E835A' }}>${data.totalIncome?.toFixed(2) || '0.00'}</Text>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ fontSize: 10, color: '#333' }}>Total Expense</Text>
            <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#d32f2f' }}>${data.totalExpense?.toFixed(2) || '0.00'}</Text>
          </View>
          <View style={{ borderTopWidth: 1, borderTopColor: '#ddd', marginVertical: 6 }} />
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 2 }}>
            <Text style={{ fontSize: 11, fontWeight: 'bold', color: '#333' }}>Project Balance</Text>
            <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#0E835A' }}>${data.balance?.toFixed(2) || '0.00'}</Text>
          </View>
        </View>

        {/* 9. İmza Bölümü - Name, Title, Organization, Signature çizgisi */}
        <View style={styles.signatureSection}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Name</Text>
            <Text style={styles.signatureValue}>{data.authorizedName || ''}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Title</Text>
            <Text style={styles.signatureValue}>{data.authorizedTitle || ''}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Organization</Text>
            <Text style={styles.signatureValue}>{data.authorizedOrganization || data.organizationName || ''}</Text>
          </View>
        </View>
        <View style={{ marginTop: 20, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: '#333', marginBottom: 2 }}>Signature</Text>
          <View style={{ borderBottomWidth: 1, borderBottomColor: '#333', width: 120, marginBottom: 2 }} />
        </View>

        {/* 10. Footer - En alt */}
        <View style={{ marginTop: 30, alignItems: 'center' }}>
          <Text style={{ fontSize: 8, color: '#888', marginBottom: 2 }}>
            © 2025 Software Development East Africa (SODEAF) — Innovating for a smarter Africa.
          </Text>
          <Text style={{ fontSize: 8, color: '#888' }}>
            Crafted by Eng. Mohamed Ibrahim Abdi (Moha-Qadar) | mohamedqadar280@gmail.com
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default KurbanReportPDF;
